/**
 * §1 row 5 — the firm's own name, for the surfaces that used to print one
 * firm's name on every tier (the browser tab title and the sidebar
 * wordmark).
 *
 * Same shape as `use-date-format.ts`: one read of the authenticated
 * `/api/firm/profile`, cached hard under a single react-query key so the
 * title and the wordmark share one request. The name changes roughly never.
 *
 * Blank until the read lands, and blank for a firm whose `firm_profile`
 * SystemConfig row is unfilled. Callers render NOTHING in that case rather
 * than a placeholder — a wordmark naming the wrong firm is worse than no
 * wordmark (audit §3, the same rule the public form's notice follows).
 *
 * Gated on a session: the root layout mounts this above the login page too,
 * and an unauthenticated read would 401 `UNAUTHENTICATED`, which
 * `apiClient` treats as session-fatal and bounces to /login — a reload loop
 * on the login page itself.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { getAccessToken } from '@/lib/auth/session';
import { getFirmProfileSchema, updateFirmProfile } from '@/lib/api/firm-profile';

export const firmProfileKeys = {
  root: ['firm-profile'] as const,
};

export function useFirmProfile(): { firmName: string } {
  const { data } = useQuery({
    queryKey: firmProfileKeys.root,
    queryFn: async () => {
      const profile = await apiClient.get<{ firmName?: string }>('/api/firm/profile');
      return (profile?.firmName ?? '').trim();
    },
    enabled: typeof window !== 'undefined' && getAccessToken() !== null,
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    // A failed read must never put a wrong name on the page — stay blank.
    retry: false,
  });
  return { firmName: data ?? '' };
}

// ── Firm configuration editor (Task 15, template-library-catalogue spec) ──
//
// A DIFFERENT surface from `useFirmProfile` above (the wordmark's one-string
// read of `/api/firm/profile`): these wrap `/api/admin/firm/*`
// (`lib/api/firm-profile.ts`), the full FIRM_PROFILE_SCHEMA editor at
// `/admin/firm`. Separate query key so a save here can never invalidate —
// or collide the cache shape with — the wordmark's read.
const FIRM_PROFILE_SCHEMA_KEY = ['firm-profile-schema'] as const;

export function useFirmProfileSchema() {
  return useQuery({
    queryKey: FIRM_PROFILE_SCHEMA_KEY,
    queryFn: () => getFirmProfileSchema(),
  });
}

export function useUpdateFirmProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: Record<string, string>) => updateFirmProfile(values),
    onSuccess: () => qc.invalidateQueries({ queryKey: FIRM_PROFILE_SCHEMA_KEY }),
  });
}
